import { Banner } from "@components/common/Banner";
import {
  DSCircularButton,
  DSLinkButton,
} from "@components/design-system/molecules";
import { DSCopyButton } from "@components/design-system/organisms";
import {
  faChevronDown,
  faChevronUp,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useState } from "react";
import { config, animated, useSpring } from "react-spring";

import { alert } from "../../../common/Alert";
import {
  NoteWrapper,
  TranscriptSection,
} from "../../../common/clinical/NotePDF";
import { getName } from "../../../export/utils";
import SomethingWrongModal from "../../common/SomethingWrongModal";

export function RecordingTranscriptDrawer({
  note,
  refetchNote,
  shouldShowTranscript,
  onTranscriptVisibilityChange,
}) {
  const [localShouldShow, setLocalShouldShow] = useState(false);
  const [shouldShowFlagNoteModal, setShouldShowFlagNoteModal] =
    useState(false);

  const shouldShow = shouldShowTranscript ?? localShouldShow;

  const updateVisibility = (nextValue) => {
    if (typeof shouldShowTranscript !== "boolean") {
      setLocalShouldShow(nextValue);
    }
    if (onTranscriptVisibilityChange) {
      onTranscriptVisibilityChange(nextValue);
    }
  };

  const handleToggleTranscript = () => {
    updateVisibility(!shouldShow);
  };
  const slideAnim = useSpring({
    transform: shouldShow ? "translateY(0%)" : "translateY(100%)",
    config: config.default,
  });
  const transcript = note?.transcription || "";

  const copyTranscript = () => {
    navigator.clipboard.writeText(transcript).then(() => {
      alert("info", "Copied!");
    });
  };

  const downloadTranscriptAsPDF = async () => {
    try {
      const doc = (
        <NoteWrapper
          note={note}
          patient={note?.patient}
          user={note?.user}
        >
          <TranscriptSection transcript={transcript} />
        </NoteWrapper>
      );
      const asPdf = pdf([]); // [] is important, throws an error without an argument
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();
      saveAs(
        blob,
        `Scribenote-Patient-Notebook-Transcript-for-${getName(
          note?.patient,
        )}-${moment().format("DD-MM-YYYY - hh:mm:ss")}.pdf`,
      );
    } catch {
      alert("error", "Failed to download PDF transcript");
    }
  };

  const parseTranscript = (transcript) => {
    //Split on <>{timestamp information}<>
    const parts = transcript.split(/<>([^<>]+)<>(?!.*<>)/g);

    return parts.map((part, index) => {
      //If the index is odd, it's a timestamp
      //This % logic is important in case there are multiple timestamps
      if (index % 2 === 1) {
        return (
          <strong key={index} className="mt-2 block italic">
            {part.trim()}
          </strong>
        );
      }
      //Otherwise, it's just regular transcript content
      return (
        <span className="mt-2 block" key={index}>
          {part.trim()}
        </span>
      );
    });
  };

  return (
    <>
      <SomethingWrongModal
        note={note}
        refetchNote={refetchNote}
        shouldShowFlagNoteModal={shouldShowFlagNoteModal}
        setShouldShowFlagNoteModal={setShouldShowFlagNoteModal}
      />
      <animated.div
        style={slideAnim}
        className={`absolute bottom-0 left-0 z-30 w-full flex flex-col items-center justify-center pointer-events-none ${
          shouldShow ? "z-40" : null
        }`}
      >
        <div className="flex flex-col items-center pointer-events-auto">
          <div
            className="relative flex flex-col items-center bg-primary pt-6 pb-4 px-6 rounded-t-2xl"
            style={{
              boxShadow: "0px -5px 50px -10px rgba(0,0,0,0.40)",
            }}
          >
            <button
              onClick={handleToggleTranscript}
              className="absolute -top-10 z-40 bg-brand py-2 px-8 rounded-t-2xl"
              data-testid="toggle-transcript-button"
            >
              <h4 className="text-white">
                {shouldShow ? "Hide Transcript" : "Show Transcript"}{" "}
                <FontAwesomeIcon
                  icon={shouldShow ? faChevronDown : faChevronUp}
                />
              </h4>
            </button>

            <p
              className="text-xs md:text-sm w-[300px] md:w-[600px] max-h-[50vh] overflow-y-auto whitespace-break-spaces"
              data-testid="transcript-text-box"
            >
              {note?.isHallucinating && (
                <Banner isWarning>
                  Please double-check that the transcript matches the
                  audio recording. If it's inaccurate,{" "}
                  <DSLinkButton
                    onClick={() => setShouldShowFlagNoteModal(true)}
                  >
                    Retry Processing.
                  </DSLinkButton>{" "}
                  If there are further issues, please{" "}
                  <DSLinkButton
                    onClick={() => setShouldShowFlagNoteModal(true)}
                  >
                    Flag to Support.
                  </DSLinkButton>{" "}
                  If everything looks accurate, safely ignore this
                  message.
                </Banner>
              )}
              {parseTranscript(transcript)}
            </p>
            <div className="pt-4 flex gap-4">
              <DSCopyButton
                text={transcript}
                onClick={copyTranscript}
              />
              <DSCircularButton
                size="sm"
                title="Download Transcript As PDF"
                onClick={downloadTranscriptAsPDF}
                icon={faFilePdf}
              />
            </div>
          </div>
        </div>
      </animated.div>
    </>
  );
}
